import { EXTERNAL_SCOPE_TYPES } from '../models/messaging-user';
import {
  conversationUserNames,
  firstNameAndLastNameInitial,
  rosterWithoutCurrentUser,
} from '../utils/messaging/conversation';
import { decodeText } from '../utils/messaging/format-text';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import Avatar from './avatar.gjs';
import Component from '@glimmer/component';
import FormatMessagingTimestamp from '../helpers/format-messaging-timestamp';
import styles from './conversation-list-item.module.scss';

export default class ConversationListItem extends Component {
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service intl;
  @service('spds/toast') toast;

  get isInactive() {
    return Boolean(this.messaging.groupMessagingEnabled && this.args.conversation.inactive);
  }

  get isUnread() {
    return this.messaging.currentProfile.unreadConversationIds.includes(this.args.conversation.id);
  }

  get trimmedDisplayName() {
    return this.args.conversation.displayName?.trim() || '';
  }

  get hasDisplayName() {
    return Boolean(this.trimmedDisplayName);
  }

  get userNames() {
    const names = this.sortedFilteredUsers.map(member => this.listNameFor(member));
    return this.formatRosterNames(names);
  }

  get isJustYou() {
    return this.listedUsers.length === 0;
  }

  get conversationTitle() {
    if (this.hasDisplayName) return this.trimmedDisplayName;

    return this.isJustYou ? this.intl.t('emc.conversation_list.just_you') : this.userNames;
  }

  get userNamesTooltip() {
    const fullNames = this.sortedFilteredUsers.map(user => user.name);
    return this.formatRosterNames(fullNames);
  }

  get showUserNamesTooltip() {
    if (!this.userNamesTooltip) return false;

    return this.hasDisplayName || this.args.conversation.isGroup || this.args.conversation.isCouple;
  }

  get emptyConversationPlaceholder() {
    return this.intl.t('emc.start_conversation_callout', {
      name: this.userNames,
    });
  }

  get filteredUsers() {
    const currentUser = this.messaging.currentProfile?.user;
    const conversation = this.args.conversation;
    const members = conversation.collapsedMembers ?? conversation.users;

    return rosterWithoutCurrentUser(members, currentUser);
  }

  formatRosterNames(names) {
    return conversationUserNames(
      names,
      this.args.conversation.isCouple && !this.args.conversation.isGroup
    );
  }

  // Groups shorten each roster slot. A couple stays one slot, using its couple name.
  listNameFor(member) {
    if (!this.args.conversation.isGroup) return member.name;

    if (member.isCouple) {
      return (member.members ?? [])
        .map(person => firstNameAndLastNameInitial(person.name))
        .join(' & ');
    }

    return firstNameAndLastNameInitial(member.name);
  }

  // 1:1 / couple rows keep the restricted counterpart's name. Groups with nobody
  // else current still use "Just you" (COM-1053 last-member placeholder).
  get listedUsers() {
    if (this.filteredUsers.length) {
      return this.filteredUsers;
    }

    if (this.args.conversation.isGroup) {
      return [];
    }

    const currentUser = this.messaging.currentProfile?.user;
    const previousMembers = this.args.conversation.previousMembers ?? [];
    return rosterWithoutCurrentUser(previousMembers, currentUser);
  }

  get sortedFilteredUsers() {
    const users = this.listedUsers ?? [];
    return [...users].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }

  get clinicianUser() {
    return this.filteredUsers.find(user => user.externalScope === EXTERNAL_SCOPE_TYPES.user);
  }

  get showAwayIndicator() {
    return (
      this.messaging.currentProfile?.isClient &&
      !this.args.conversation.isGroup &&
      this.clinicianUser?.isAway
    );
  }

  get showLastMessageAuthor() {
    return this.args.conversation.isGroup || this.args.conversation.isCouple;
  }

  get lastMessageAuthorPrefix() {
    const authorName = this.args.conversation.lastMessage?.user?.name;
    if (!this.showLastMessageAuthor || !authorName) return '';
    return `${firstNameAndLastNameInitial(authorName)}: `;
  }

  openConversationTask = task({ drop: true }, async () => {
    try {
      const conversation = await this.messaging.persistConversation(this.args.conversation);
      this.messagingNavigation.toConversation(conversation);
    } catch (err) {
      this.messagingLogging.error(err);
      this.toast.error(this.intl.t('emc.general_error'));
    }
  });

  openConversation = () => this.openConversationTask.perform();

  <template>
    <li
      class="conversation-list-item"
      data-test-conversation={{@conversation.id}}
      data-test-inactive-conversation={{if this.isInactive "true"}}
    >
      <button
        type="button"
        class="{{styles.component}} {{if this.isInactive 'inactive'}}"
        {{on "click" this.openConversation}}
      >
        <Avatar
          @users={{this.sortedFilteredUsers}}
          @isGroup={{@conversation.isGroup}}
          @isCouple={{@conversation.isCouple}}
          @inactive={{this.isInactive}}
        />

        <div class="item-details">
          <div class="top-line">
            {{#if this.isUnread}}
              <span
                class="unread-dot"
                aria-label={{t "emc.unread_message_indicator"}}
                data-test-unread-indicator
              ></span>
            {{/if}}
            <span class="user-names" data-test-user-names>
              {{this.conversationTitle}}
              {{#if this.showUserNamesTooltip}}
                <AttachTooltip
                  @placement="bottom"
                  @arrow={{true}}
                  @animation="fade"
                  @class="emc-tooltip"
                >
                  <span data-test-user-names-tooltip>{{this.userNamesTooltip}}</span>
                </AttachTooltip>
              {{/if}}
              {{#if this.showAwayIndicator}}
                <span class="away-indicator" data-test-away-indicator>
                  {{t "emc.away_status.indicator"}}
                </span>
              {{/if}}
            </span>
            <span class="timestamp" data-test-timestamp>
              {{FormatMessagingTimestamp @conversation.updatedAt}}
            </span>
          </div>
          <p class="last-message" data-test-last-message>
            {{#if @conversation.hasMessageDraft}}
              <span class="draft-indicator" data-test-draft-indicator>
                {{t "emc.draft_indicator"}}
              </span>
              {{decodeText @conversation.messageDraft}}
            {{else if @conversation.lastMessage}}
              {{this.lastMessageAuthorPrefix}}{{decodeText @conversation.lastMessage.text}}
            {{else}}
              <span class="empty-conversation-callout">
                {{this.emptyConversationPlaceholder}}
              </span>
            {{/if}}
          </p>
        </div>
      </button>
    </li>
  </template>
}
